<template>
    <div class="pp-card">
        <div class="pp-card__head">
            <span class="pp-status" :class="`pp-status--${status.tone}`">
                <v-icon x-small>{{ status.icon }}</v-icon>
                {{ status.label }}
            </span>
            <span class="pp-card__when">{{ formatDate(request.createdAt, 'D/M/YYYY') }}</span>
        </div>

        <router-link :to="{ name: 'Match', params: { id: request.matchId } }" class="pp-card__teams">
            <span v-for="team in teams" :key="team.id" class="pp-card__team" :class="{ 'pp-card__team--requester': team.id === request.teamId }">
                <span class="pp-card__logo">
                    <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt="">
                    <v-icon v-else small color="orange">mdi-basketball</v-icon>
                </span>
                <span>{{ team.name }}</span>
            </span>
        </router-link>
        <div class="pp-card__time">
            <v-icon x-small color="orange darken-2">mdi-calendar-outline</v-icon>
            {{ formatDate(request.matchDatetime) }}<span v-if="request.arenaName"> · {{ request.arenaName }}</span>
        </div>

        <div class="pp-card__meta">Αίτημα αναβολής από <b>{{ request.teamName }}</b></div>
        <div v-if="request.status === 'cancelled' && request.cancelledAt" class="pp-card__time mt-1">
            Ακυρώθηκε στις {{ formatDate(request.cancelledAt) }}
        </div>

        <!-- the captain of the team that made it, while it waits for the league -->
        <div v-if="canCancel" class="pp-card__actions">
            <v-btn small text color="grey darken-2" :loading="cancelling" @click="confirmCancel = true">
                <v-icon small left>mdi-cancel</v-icon>Ακύρωση αιτήματος
            </v-btn>
        </div>
        <div v-if="error" class="red--text text-body-2 mt-2">{{ error }}</div>

        <v-dialog v-model="confirmCancel" max-width="380">
            <v-card>
                <v-card-title class="font-weight-bold">Ακύρωση του αιτήματος;</v-card-title>
                <v-card-text>Ο αγώνας θα γίνει κανονικά και η αναβολή επιστρέφει στην ομάδα. Το αίτημα θα μείνει στο ιστορικό ως ακυρωμένο.</v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text @click="confirmCancel = false">Πίσω</v-btn>
                    <v-spacer />
                    <v-btn depressed color="red darken-1" class="white--text" @click="confirmCancel = false; cancel()">Ακύρωση αιτήματος</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import postponementService from '@/services/postponement'
    import { errorMessage, formatDate, isMyRequest } from '@/utils/matchChange'

    const STATUS = {
        pending: { label: 'Αναμένει έγκριση διοργάνωσης', icon: 'mdi-timer-sand', tone: 'warn' },
        approved: { label: 'Εγκρίθηκε · ο αγώνας αναβλήθηκε', icon: 'mdi-check-circle-outline', tone: 'ok' },
        rejected: { label: 'Δεν εγκρίθηκε', icon: 'mdi-close-circle-outline', tone: 'bad' },
        cancelled: { label: 'Ακυρώθηκε από την ομάδα', icon: 'mdi-cancel', tone: 'muted' }
    }

    // A postponement request of a team captain and where it stands
    export default {
        name: 'PostponementCard',
        props: {
            request: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                confirmCancel: false,
                cancelling: false,
                error: null
            }
        },
        computed: {
            status() {
                return STATUS[this.request.status] || { label: this.request.status, icon: 'mdi-information-outline', tone: 'muted' }
            },
            canCancel() {
                return this.request.status === 'pending' && isMyRequest(this.$store, this.request.teamId)
            },
            teams() {
                return [
                    { id: this.request.team1Id, name: this.request.team1Name, logo: this.request.team1Logo },
                    { id: this.request.team2Id, name: this.request.team2Name, logo: this.request.team2Logo }
                ]
            }
        },
        methods: {
            formatDate,
            cancel() {
                this.cancelling = true
                this.error = null
                postponementService.cancelPostponement(this.request.id, {
                    teamId: this.$store.getters.getTeam.id,
                    playerId: this.$store.getters.getPlayer.id
                }).then(resp => {
                    this.$emit('updated', resp.data)
                }).catch(error => {
                    this.error = errorMessage(error, 'Δεν ήταν δυνατή η ακύρωση του αιτήματος.')
                }).finally(() => {
                    this.cancelling = false
                })
            }
        }
    }
</script>

<style scoped>
.pp-card {
    padding: 16px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    text-align: left;
}

.pp-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.pp-card__when {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.5);
}

.pp-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 800;
}

.pp-status .v-icon {
    color: inherit !important;
}

.pp-status--warn {
    background: #fff3e0;
    color: #e65100;
}

.pp-status--ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.pp-status--bad {
    background: #ffebee;
    color: #c62828;
}

.pp-status--muted {
    background: #eceff1;
    color: #546e7a;
}

.pp-card__teams {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    color: inherit !important;
    text-decoration: none;
}

.pp-card__team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-weight: 700;
    word-break: break-word;
}

.pp-card__team + .pp-card__team::before {
    content: '–';
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.4);
}

.pp-card__logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
}

.pp-card__logo img {
    max-width: 100%;
    max-height: 100%;
}

.pp-card__time {
    margin-top: 2px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
}

.pp-card__actions {
    margin-top: 12px;
}

.pp-card__actions .v-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 700;
}

.pp-card__meta {
    margin-top: 12px;
    font-size: 0.85rem;
    word-break: break-word;
}
</style>
